// What the holder of an editable task link can do to one task: correct its
// details, mark it complete, add photos, and take back a photo they added.
//
// THE ROW STAYS DATA. `.claude/rules/tasks.md` says `readOnly` is how a shared
// view renders a row as data rather than a button that does nothing, and that is
// still true here: there is nowhere for a shared row to navigate, so the new
// capability lives in these explicit controls on the row's continuation cell,
// never in an onClick that turns the row back into a button.
//
// IT HAS A SAVE BUTTON, which contradicts the app-wide "Autosave, no Save
// buttons" rule. That rule is about SaveQueue over AppSync records, and this page
// has neither: no Amplify client, no lanes, no useAutosave, and a write that goes
// through a public Function URL and can be refused with a sentence the visitor
// needs to read. Debouncing keystrokes into an anonymous write endpoint would
// also turn one person's typing into a stream of DynamoDB writes. So: a Save
// button, said here so nobody "fixes" it.
//
// COMPLETE ASKS FIRST, and the confirm NAMES THE OUTCOME. A recurring task moves
// to its next period; a one-off is deleted outright, and there is no undo for
// that anywhere in the app. The two are told apart by the same predicate the
// Lambda uses, so the dialog cannot promise one and get the other.
//
// WHAT CAN BE EDITED is the endpoint's whitelist, filtered out of the owner's own
// `taskFields` so the labels and input types are the ones the owner sees. It is
// spelled here as a list of KEYS rather than rebuilt: the endpoint refuses
// anything off its list by name, so a field added here that it does not accept
// would fail loudly rather than silently, which is the right way round.

import { useCallback, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { Confirm } from '../../components';
import { taskFields } from '../../fleet-shared/TaskFields';
import { validateTask } from '../../fleet-shared/TaskValidation.mjs';
import { isRecurring, nextDueDate, nextDueCounter } from '../../fleet-shared/TaskRecurrence.mjs';
import {
  databaseDateTimeString,
  dateStringToDate,
  displayDate,
} from '../../fleet-shared/Common.mjs';
import {
  MAX_GUEST_UPLOAD_FILES,
  MAX_GUEST_UPLOAD_FILE_SIZE,
} from '../../fleet-shared/Attachments.mjs';
import {
  completeSharedTask,
  editSharedTask,
  uploadSharedTaskPhotos,
} from '../../utilities/shareTasksWrite.mjs';

// The endpoint's WRITABLE_TASK_FIELDS, in the order the owner's form shows them.
// `itemCounterId` is deliberately absent: it names a counter the visitor never
// sees, and validateTask couples it to dueCounter.
const EDITABLE_KEYS = [
  'name',
  'description',
  'notes',
  'dueDate',
  'dueCounter',
  'priority',
  'counterInterval',
  'daysInterval',
  'monthsInterval',
];

// The formats the endpoint will sign for. Spelled as MIME types because that is
// what a file dialog filters on; the rule itself is guestUploadRejection's, and
// on iOS this is also what gets a HEIC converted to JPEG at pick time rather
// than handed to us as a file no browser can decode.
const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';
const MB = (bytes) => Math.round(bytes / (1024 * 1024));

const editableFields = taskFields.filter((field) => EDITABLE_KEYS.includes(field.key));

// A form value for one field. Numbers come back as strings from an <input>, and
// the endpoint refuses NaN by name -- so an emptied number is null, not ''.
//
// `dueDate` is narrowed to `YYYY-MM-DD` because that is the only thing an
// `<input type="date">` will display. `storedValue` below is what puts it back.
function valueFor(task, field) {
  const stored = task[field.key];
  if (stored === null || stored === undefined) return '';
  if (field.key === 'dueDate') return String(stored).slice(0, 10);
  return String(stored);
}

/**
 * One form value in the spelling the database stores.
 *
 * THE DATE CONVERSION IS THE WHOLE POINT, AND IT BELONGS HERE RATHER THAN IN THE
 * LAMBDA. `dueDate` is an `AWSDateTime` whose offset is mandatory and a GSI sort
 * key (`tasksByItemIdAndDueDate`) compared as a string, but the date input only
 * ever hands back a bare `2026-01-31`. Sent as-is, AppSync rejects it against
 * the scalar, the mutation returns nothing, and the endpoint cannot tell that
 * apart from a revoked link -- so the visitor is told the list is gone and the
 * edit silently never lands.
 *
 * `databaseDateTimeString(dateStringToDate(...))` is exactly what the owner's own
 * form writes (`Task.jsx`), and doing it in the BROWSER is the point: this is the
 * only side that knows the user's zone. A Lambda normalizing the same value could
 * only stamp its own UTC offset into that sort key, which is the hazard
 * TaskRecurrence.mjs' header exists to warn about -- so the endpoint refuses a
 * bare date instead of guessing, and this is what stops one being sent.
 */
function storedValue(field, raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  if (field.type === 'number') return Number(raw);
  if (field.key === 'dueDate') return databaseDateTimeString(dateStringToDate(raw));
  return raw;
}

/**
 * ONLY WHAT CHANGED, compared against the values the form was seeded with.
 *
 * Submitting every editable column on every save was the other half of the bug
 * above: editing just the notes resubmitted the due date, so an untouched date
 * still had to survive the round trip. It is also last-write-wins over the whole
 * row -- a visitor who opened the form before the owner changed a priority would
 * put the stale one back on save.
 *
 * Compared as FORM values, not stored ones, so clearing a field is a real change
 * to null and still sent.
 */
function payloadFrom(form, initial) {
  const fields = {};
  for (const field of editableFields) {
    const raw = form[field.key];
    if (raw === initial[field.key]) continue;
    fields[field.key] = storedValue(field, raw);
  }
  return fields;
}

// What the confirm promises. Computed with the SAME functions the Lambda rolls
// forward with, so the dialog cannot say "due again in June" and then delete the
// row.
function completionOutcome(task) {
  if (!isRecurring(task)) {
    return 'This task will be marked done and removed from the list.';
  }
  const date = nextDueDate(task);
  const counter = nextDueCounter(task);
  const parts = [];
  if (date) parts.push(`due again on ${displayDate(date)}`);
  if (counter !== null) parts.push(`due again at ${counter}`);
  return `This task will be marked done and ${parts.join(' and ')}.`;
}

export function SharedTaskActions({
  shareId,
  task,
  editorName,
  onTaskChanged,
  onTaskRemoved,
  onPhotosAdded,
  onGone,
}) {
  const [showEdit, setShowEdit] = useState(false);
  // Bumped on OPEN only, and it is half of EditTaskModal's `key` -- see the
  // comment at that element for what it is for and why it is not `showEdit`.
  const [editOpenCount, setEditOpenCount] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [outcome, setOutcome] = useState(null);

  // Every write funnels through here so `gone` is handled in one place: a link
  // revoked, or a task reassigned or completed by someone else, while this page
  // was open is a fact about the whole page, not about the button that found it.
  const run = useCallback(
    async (work) => {
      setBusy(true);
      setError(null);
      setOutcome(null);
      try {
        return await work();
      } catch (err) {
        if (err?.gone && onGone) onGone();
        else setError(err);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [onGone]
  );

  const save = useCallback(
    async (fields) => {
      const result = await run(() =>
        editSharedTask({ shareId, taskId: task.id, fields, editorName })
      );
      if (result?.task) {
        onTaskChanged(result.task);
        setShowEdit(false);
      }
    },
    [run, shareId, task.id, editorName, onTaskChanged]
  );

  const complete = useCallback(async () => {
    setConfirming(false);
    const result = await run(() =>
      completeSharedTask({ shareId, taskId: task.id, editorName })
    );
    if (!result) return;
    if (result.outcome === 'deleted') onTaskRemoved(task.id);
    else if (result.task) onTaskChanged(result.task);
  }, [run, shareId, task.id, editorName, onTaskChanged, onTaskRemoved]);

  const addPhotos = useCallback(
    async (files, input) => {
      setProgress({ done: 0, total: files.length });
      const result = await run(() =>
        uploadSharedTaskPhotos({
          shareId,
          taskId: task.id,
          files,
          editorName,
          onProgress: setProgress,
        })
      );
      setProgress(null);
      // So picking the same file again re-fires onChange.
      if (input) input.value = '';
      if (!result) return;
      if (result.photos.length > 0) onPhotosAdded(task.id, result.photos);
      setOutcome(result);
    },
    [run, shareId, task.id, editorName, onPhotosAdded]
  );

  const uploading = progress !== null;

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={busy}
        onClick={() => {
          setEditOpenCount((n) => n + 1);
          setShowEdit(true);
        }}
      >
        Edit
      </Button>
      <Button size="sm" variant="outline-success" disabled={busy} onClick={() => setConfirming(true)}>
        Complete
      </Button>

      <Form.Group controlId={`shared-task-photos-${task.id}`} className="mb-0">
        <Form.Label className="visually-hidden">Add photos to {task.name}</Form.Label>
        <Form.Control
          size="sm"
          type="file"
          multiple
          accept={ACCEPT}
          disabled={busy}
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            if (picked.length > 0) addPhotos(picked, e.target);
          }}
        />
      </Form.Group>

      {uploading && (
        <div className="flex-grow-1" style={{ minWidth: 120 }}>
          <ProgressBar
            style={{ height: 4 }}
            now={progress.total ? (progress.done / progress.total) * 100 : 0}
          />
        </div>
      )}

      {error && (
        <span className="text-danger small w-100">{error.message || String(error)}</span>
      )}

      {outcome && <UploadOutcome result={outcome} />}

      {/* KEYED SO EACH OPEN GETS A FRESH FORM.
          EditTaskModal is rendered unconditionally and so never unmounts on its
          own: `show` only unmounts the <Modal>'s DOM, not the `form` state that
          lives one level above it. Without this key, a visitor who edited a
          field, hit Cancel and reopened the dialog would find their discarded
          text still there -- and Save would compare it against the memoized
          `initial`, see a difference, and submit the change they thought they had
          thrown away.
          The count rather than `showEdit`: keying on the flag would also remount
          on CLOSE, which drops Bootstrap's fade-out. This advances only on open,
          so both transitions survive. `task.id` is in the key too, so a different
          row never inherits the last one's form. */}
      <EditTaskModal
        key={`${task.id}-${editOpenCount}`}
        show={showEdit}
        task={task}
        busy={busy}
        onHide={() => setShowEdit(false)}
        onSave={save}
      />

      <Confirm
        show={confirming}
        title="Complete this task?"
        // NAMES the outcome: a one-off is removed outright and there is no undo.
        body={completionOutcome(task)}
        confirmText="Complete"
        onConfirm={complete}
        onHide={() => setConfirming(false)}
      />
    </div>
  );
}

// The fields the endpoint accepts, laid out with the owner's own labels.
//
// Held locally and submitted whole rather than debounced onto a queue -- see
// this file's header for why this is the one Save button in the app.
function EditTaskModal({ show, task, busy, onHide, onSave }) {
  const initial = useMemo(() => {
    const state = {};
    for (const field of editableFields) state[field.key] = valueFor(task, field);
    return state;
  }, [task]);

  // Seeded once per mount, and the PARENT's `key` is what guarantees a mount per
  // open -- this component is rendered unconditionally, so `show` going false
  // unmounts the <Modal>'s DOM and leaves this state untouched. Reseeding it here
  // with an effect would be the same thing said less reliably.
  const [form, setForm] = useState(initial);
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = () => {
    const fields = payloadFrom(form, initial);
    if (Object.keys(fields).length === 0) {
      // Nothing was touched. The endpoint refuses an empty change set by design,
      // so closing is both the honest answer and the one that does not put a
      // "No changes were sent." error in front of someone who just hit Cancel's
      // neighbour.
      onHide();
      return;
    }
    // The SAME validateTask the owner's edit form holds a save on, so a visitor
    // is told what is wrong before a round trip rather than after one. Applied to
    // the task as it would be AFTER this edit, which is why `fields` being a
    // partial matters: an untouched column keeps its stored value here exactly as
    // it will on the server. The endpoint checks it again regardless -- this is
    // the courtesy, not the rule.
    const check = validateTask({ ...task, ...fields });
    if (!check.ok) {
      setFieldErrors(check.fieldErrors);
      return;
    }
    setFieldErrors({});
    onSave(fields);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="h5">Edit task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {editableFields.map((field) => (
          <Form.Group className="mb-3" controlId={`shared-edit-${field.key}`} key={field.key}>
            <Form.Label className="small">{field.name}</Form.Label>
            <Form.Control
              type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
              step={field.step}
              value={form[field.key] ?? ''}
              isInvalid={Boolean(fieldErrors[field.key])}
              disabled={busy}
              onChange={(e) => set(field.key, e.target.value)}
            />
            {fieldErrors[field.key] && (
              <Form.Control.Feedback type="invalid">
                {fieldErrors[field.key]}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} disabled={busy}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// What actually happened, per file where it differs. A batch is routinely partly
// good, and "4 photos added" while one silently vanished is the version people
// stop trusting. SharedPhotoUpload's rule and its shape.
function UploadOutcome({ result }) {
  const added = result.photos.length;
  const trouble = [...(result.rejected ?? []), ...(result.failed ?? [])];

  return (
    <div className="small w-100">
      {added > 0 && (
        <p className="text-success mb-1">
          {added === 1 ? '1 photo added' : `${added} photos added`}
          {result.notified ? ', and the owner has been emailed.' : '.'}
        </p>
      )}
      {trouble.length > 0 && (
        <ul className="text-body-secondary mb-0 ps-3">
          {trouble.map((item) => (
            <li key={`${item.name}-${item.reason}`}>
              {item.name}: {item.reason}
            </li>
          ))}
        </ul>
      )}
      {added === 0 && trouble.length === 0 && (
        <p className="text-body-secondary mb-0">
          Up to {MAX_GUEST_UPLOAD_FILES} photos at a time, {MB(MAX_GUEST_UPLOAD_FILE_SIZE)} MB
          each.
        </p>
      )}
    </div>
  );
}

export default SharedTaskActions;
